import { List } from "immutable";
import { createSelector } from "reselect";

import { calculateAverageOfSectionScores } from "seneca-common/features/session/logic/courseScore";
import { calculateAverageContentProficiency } from "seneca-common/features/session/logic/proficiency";
import {
  getContentStats,
  getSectionStats,
  SectionStats,
  sectionStatsSelectors
} from "seneca-common/features/stats/state";

import {
  getAllSectionContentIds,
  getAllSections,
  getDescendantLeafSectionIds,
  makeGetDescendantLeafSectionIds,
  SectionId,
  Sections,
  sectionsSelectors
} from "../state";
import { getAllSectionExerciseContentIds } from "../state/selectors/state";

type State = any;

export function makeGetSectionBestScore(
  sectionId: SectionId
): (arg0: State) => number {
  const getDescendantLeafSectionIds =
    makeGetDescendantLeafSectionIds(sectionId);
  return createSelector(
    getAllSections,
    getSectionStats,
    getDescendantLeafSectionIds,
    (sections, stats, leafSectionIds) =>
      // @ts-ignore
      getSectionBestScoreImpl(sections, stats, leafSectionIds, sectionId)
  );
}

// Use makeGetSectionBestScore instead if possible
export function getSectionBestScore(
  state: State,
  sectionId: SectionId
): number {
  return getSectionBestScoreImpl(
    getAllSections(state),
    // @ts-ignore
    getSectionStats(state),
    getDescendantLeafSectionIds(getAllSections(state), sectionId),
    sectionId
  );
}

function getSectionBestScoreImpl(
  sections: Sections,
  stats: SectionStats,
  studyableSectionIds: List<SectionId>,
  sectionId: SectionId
): number {
  if (sectionsSelectors.isLeafSection(sections, sectionId)) {
    return sectionStatsSelectors.hasSectionStat(stats, sectionId)
      ? sectionStatsSelectors.getBestScore(stats, sectionId)
      : 0;
  } else {
    const sectionStats = sectionStatsSelectors.getSectionsStats(
      stats,
      studyableSectionIds
    );
    return calculateAverageOfSectionScores({
      sectionStats: sectionStats,
      totalSections: studyableSectionIds.size
    });
  }
}

export function getSectionProficiency(
  state: State,
  sectionId: SectionId
): number {
  const exerciseContentIds = getAllSectionExerciseContentIds(state, sectionId);
  const normalContentIds = getAllSectionContentIds(state, sectionId);
  const contentIds = exerciseContentIds.merge(normalContentIds);

  const contentStats = getContentStats(state, contentIds).toList();

  return calculateAverageContentProficiency(contentStats, contentIds.size);
}
